// src/pages/PaymentEntryPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { updatePayment, getMemberDirectory, getDuesForMember } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY  = '#1B3A6B';
const GOLD  = '#C9A84C';
const RED   = '#c0392b';
const GREEN = '#1a6b3a';

export default function PaymentEntryPage() {
  const navigate      = useNavigate();
  const { role }      = useAuth();

  const canAccess = role === 'treasurer' || role === 'executive';

  const [members,      setMembers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const [memberDues,   setMemberDues]   = useState([]);
  const [loadingDues,  setLoadingDues]  = useState(false);

  const [search,         setSearch]         = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDropdown,   setShowDropdown]   = useState(false);

  const [form, setForm] = useState({
    type:      'dues',
    amount:    '',
    currency:  'GHS',
    date:      new Date().toISOString().split('T')[0],
    reference: '',
    year:      new Date().getFullYear().toString(),
    notes:     ''
  });

  const CURRENCIES = ['GHS', 'USD', 'GBP', 'EUR', 'CAD', 'AUD'];
  const YEARS = Array.from({ length: 6 }, (_, i) =>
    (new Date().getFullYear() - i).toString()
  );

  useEffect(() => {
    if (!canAccess) {
      navigate('/unauthorized');
      return;
    }
    loadMembers();
  }, [canAccess, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMembers() {
    try {
      const result = await getMemberDirectory();
      setMembers(result.members || []);
    } catch (err) {
      setError('Could not load members: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredMembers = search.trim()
    ? members.filter(m =>
        m.name?.toLowerCase().includes(search.toLowerCase()) ||
        m.nickname?.toLowerCase().includes(search.toLowerCase()) ||
        m.boardingHouse?.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 8)
    : [];

  const handleSelectMember = async (member) => {
    setSelectedMember(member);
    setSearch(member.name);
    setShowDropdown(false);
    setError('');
    setMemberDues([]);

    // Load this member's dues
    setLoadingDues(true);
    try {
      const result = await getDuesForMember(member.id);
      setMemberDues(result.dues || []);
    } catch (err) {
      console.error('Could not load member dues:', err);
    } finally {
      setLoadingDues(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!selectedMember) {
      setError('Please select a member.');
      return;
    }
    if (!form.amount || isNaN(Number(form.amount)) ||
        Number(form.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (!form.date) {
      setError('Please enter a payment date.');
      return;
    }

    setSaving(true);
    try {
      await updatePayment({
        memberId:  selectedMember.id,
        type:      form.type,
        amount:    Number(form.amount),
        currency:  form.currency,
        date:      form.date,
        reference: form.reference,
        year:      form.type === 'dues' ? form.year : undefined,
        notes:     form.notes
      });

      setSuccess(
        `Payment of ${form.currency} ${form.amount} recorded for ${selectedMember.name}.`
      );

      // Refresh dues after payment
      const result = await getDuesForMember(selectedMember.id);
      setMemberDues(result.dues || []);

      setForm({
        type:      'dues',
        amount:    '',
        currency:  'GHS',
        date:      new Date().toISOString().split('T')[0],
        reference: '',
        year:      new Date().getFullYear().toString(),
        notes:     ''
      });

      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      setError('Failed to record payment: ' + err.message);
    } finally {
      setSaving(false);
    }
  };


  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f0f4f8', fontFamily: 'Arial, sans-serif'
      }}>
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '700px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff'
        }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
            💳 Record Payment
          </h2>
          <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
            Treasurer & Executive access only
          </p>
        </div>

        {/* Success */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '16px',
            marginBottom: '16px', color: GREEN,
            fontSize: '14px', fontWeight: '600'
          }}>
            ✓ {success}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Form Card */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>

          {/* Step 1 — Select Member */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              color: NAVY, margin: '0 0 16px', fontSize: '15px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${GOLD}`
            }}>
              Step 1 — Select Member
            </h3>

            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setSelectedMember(null);
                  setMemberDues([]);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search by name, nickname or house..."
                style={{
                  width: '100%', padding: '11px 14px',
                  border: `2px solid ${selectedMember
                    ? GOLD : '#e0e0e0'}`,
                  borderRadius: '8px', fontSize: '14px',
                  outline: 'none', boxSizing: 'border-box'
                }}
              />

              {/* Dropdown */}
              {showDropdown && filteredMembers.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%',
                  left: 0, right: 0, background: '#fff',
                  border: '2px solid #e0e0e0',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 100, maxHeight: '240px',
                  overflowY: 'auto'
                }}>
                  {filteredMembers.map(m => (
                    <div
                      key={m.id}
                      onClick={() => handleSelectMember(m)}
                      style={{
                        padding: '12px 16px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center',
                        gap: '12px',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={e =>
                        e.currentTarget.style.background = '#f0f4f8'
                      }
                      onMouseLeave={e =>
                        e.currentTarget.style.background = '#fff'
                      }
                    >
                      <div style={{
                        width: '36px', height: '36px',
                        borderRadius: '50%', background: NAVY,
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '14px',
                        fontWeight: 'bold', color: GOLD, flexShrink: 0
                      }}>
                        {getInitials(m.name)}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: '600', fontSize: '14px',
                          color: '#333'
                        }}>
                          {m.name}
                          {m.nickname &&
                            <span style={{
                              color: GOLD, fontStyle: 'italic',
                              marginLeft: '6px', fontSize: '13px'
                            }}>
                              "{m.nickname}"
                            </span>
                          }
                        </div>
                        <div style={{
                          fontSize: '12px', color: '#888'
                        }}>
                          {m.boardingHouse} House
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected member card */}
            {selectedMember && (
              <div style={{
                marginTop: '12px', padding: '12px 16px',
                background: '#f0f4f8', borderRadius: '8px',
                display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: NAVY, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', fontWeight: 'bold',
                  color: GOLD, flexShrink: 0
                }}>
                  {getInitials(selectedMember.name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 'bold', color: NAVY, fontSize: '15px'
                  }}>
                    {selectedMember.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedMember.boardingHouse} House
                    {selectedMember.occupation &&
                      ` · ${selectedMember.occupation}`}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedMember(null);
                    setSearch('');
                    setMemberDues([]);
                  }}
                  style={{
                    background: 'none', border: 'none',
                    color: '#aaa', cursor: 'pointer',
                    fontSize: '18px', padding: '4px'
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Member Dues Summary */}
            {selectedMember && (
              <div style={{
                marginTop: '12px', background: '#fff',
                border: '2px solid #e0e0e0', borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '10px 16px', background: '#f8f9fa',
                  borderBottom: '1px solid #e0e0e0',
                  fontSize: '13px', fontWeight: '600', color: NAVY
                }}>
                  Outstanding Dues for {selectedMember.name}
                </div>

                {loadingDues ? (
                  <div style={{
                    padding: '12px 16px',
                    fontSize: '13px', color: '#888'
                  }}>
                    Loading dues...
                  </div>
                ) : memberDues.length === 0 ? (
                  <div style={{
                    padding: '12px 16px',
                    fontSize: '13px', color: '#888'
                  }}>
                    No dues records found for this member.
                  </div>
                ) : (
                  <table style={{
                    width: '100%', borderCollapse: 'collapse',
                    fontSize: '13px'
                  }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        {['Year','Owed','Paid','Balance','Status']
                          .map(h => (
                          <th key={h} style={{
                            padding: '8px 12px', textAlign: 'left',
                            color: '#888', fontWeight: '600',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '11px',
                            textTransform: 'uppercase'
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {memberDues
                        .sort((a, b) => b.year - a.year)
                        .map((due, i) => {
                          const owed    = Number(due.amountOwed) || 0;
                          const paid    = Number(due.amountPaid) || 0;
                          const balance = owed - paid;
                          const settled = balance <= 0;
                          return (
                            <tr key={i} style={{
                              borderBottom: '1px solid #f0f0f0',
                              background: settled
                                ? '#f9fff9' : '#fffaf9'
                            }}>
                              <td style={{
                                padding: '10px 12px',
                                fontWeight: 'bold', color: NAVY
                              }}>
                                {due.year}
                              </td>
                              <td style={{
                                padding: '10px 12px', color: '#333'
                              }}>
                                {owed.toLocaleString()}
                              </td>
                              <td style={{
                                padding: '10px 12px',
                                color: GREEN, fontWeight: '600'
                              }}>
                                {paid.toLocaleString()}
                              </td>
                              <td style={{
                                padding: '10px 12px',
                                color: settled ? GREEN : RED,
                                fontWeight: 'bold'
                              }}>
                                {balance.toLocaleString()}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={{
                                  background: settled
                                    ? '#d4edda' : '#fdecea',
                                  color: settled ? GREEN : RED,
                                  padding: '2px 8px',
                                  borderRadius: '8px',
                                  fontSize: '11px', fontWeight: 'bold'
                                }}>
                                  {settled ? '✓ Settled' : 'Outstanding'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Step 2 — Payment Details */}
          <div>
            <h3 style={{
              color: NAVY, margin: '0 0 16px', fontSize: '15px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${GOLD}`
            }}>
              Step 2 — Payment Details
            </h3>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px'
            }}>

              {/* Payment Type */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '8px'
                }}>
                  Payment Type
                </label>
                <div style={{
                  display: 'flex', gap: '8px', flexWrap: 'wrap'
                }}>
                  {[
                    { value: 'dues',        label: '📋 Dues' },
                    { value: 'activity',    label: '🎉 Activity' },
                    { value: 'fundraising', label: '🎯 Fundraising' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleChange('type', opt.value)}
                      style={{
                        padding: '8px 16px',
                        background: form.type === opt.value
                          ? NAVY : '#f0f4f8',
                        color: form.type === opt.value
                          ? '#fff' : '#555',
                        border: `2px solid ${form.type === opt.value
                          ? NAVY : '#e0e0e0'}`,
                        borderRadius: '8px', cursor: 'pointer',
                        fontSize: '13px', fontWeight: '600'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dues Year */}
              {form.type === 'dues' && (
                <div>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    Dues Year
                  </label>
                  <select
                    value={form.year}
                    onChange={e =>
                      handleChange('year', e.target.value)
                    }
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', background: '#fff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {YEARS.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Amount + Currency */}
              <div className="payment-amount-row" style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={e =>
                      handleChange('amount', e.target.value)
                    }
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ width: '120px' }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    Currency
                  </label>
                  <select
                    value={form.currency}
                    onChange={e =>
                      handleChange('currency', e.target.value)
                    }
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', background: '#fff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {CURRENCIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => handleChange('date', e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Reference */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Reference / Transaction ID
                </label>
                <input
                  type="text"
                  value={form.reference}
                  onChange={e =>
                    handleChange('reference', e.target.value)
                  }
                  placeholder="Bank ref, mobile money ID, cheque no..."
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Notes */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Notes (optional)
                </label>
                <textarea
                  value={form.notes}
                  onChange={e =>
                    handleChange('notes', e.target.value)
                  }
                  placeholder="Any additional notes..."
                  rows={3}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>

              {/* Payment Summary */}
              {selectedMember && form.amount && (
                <div style={{
                  background: '#f0f4f8', borderRadius: '8px',
                  padding: '16px', border: `2px solid ${NAVY}`
                }}>
                  <div style={{
                    fontWeight: 'bold', color: NAVY,
                    marginBottom: '8px', fontSize: '14px'
                  }}>
                    Payment Summary
                  </div>
                  <div style={{
                    fontSize: '13px', color: '#555',
                    display: 'flex', flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div>
                      <strong>Member:</strong> {selectedMember.name}
                    </div>
                    <div>
                      <strong>Type:</strong> {form.type}
                      {form.type === 'dues' && ` (${form.year})`}
                    </div>
                    <div>
                      <strong>Amount:</strong> {form.currency}{' '}
                      {Number(form.amount).toLocaleString()}
                    </div>
                    <div>
                      <strong>Date:</strong> {form.date}
                    </div>
                    {form.reference && (
                      <div>
                        <strong>Reference:</strong> {form.reference}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  width: '100%', padding: '14px',
                  background: saving ? '#ccc' : NAVY,
                  color: '#fff', border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '16px', fontWeight: 'bold',
                  marginTop: '8px'
                }}
              >
                {saving ? 'Recording Payment...' : 'Record Payment'}
              </button>

              {saving && (
                <p style={{
                  fontSize: '12px', color: '#888',
                  textAlign: 'center', margin: 0
                }}>
                  This may take a few seconds...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}